import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, ScrollText, Download, ArrowUp, ArrowDown, Mail } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";

const SYM = { USD: "$", NGN: "₦" };

export default function MemosPage({ mode = "customer" }) {
  const isAR = mode === "customer";
  const partyLabel = isAR ? "Customer" : "Vendor";
  const endpoint = isAR ? "/customer-memos" : "/vendor-memos";
  const partyEndpoint = isAR ? "/customers" : "/vendors";
  const applyEndpoint = isAR ? "/invoices" : "/bills";

  const [memos, setMemos] = useState([]);
  const [parties, setParties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailMemo, setEmailMemo] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: "", subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [form, setForm] = useState({
    party_id: "", memo_type: "credit", date: new Date().toISOString().slice(0, 10),
    currency: "USD", reason: "", apply_to_id: "none",
    lines: [{ _k: crypto.randomUUID(), description: "", quantity: "1", unit_price: "" }],
  });

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: p }] = await Promise.all([
        api.get(endpoint),
        api.get(partyEndpoint),
      ]);
      setMemos(m || []);
      setParties(p || []);
    } catch (err) {
      toast.error(`Couldn't load ${partyLabel} memos`);
    }
  }, [endpoint, partyEndpoint, partyLabel]);

  useEffect(() => { load(); }, [load]);

  // When the party changes and it's a credit memo, load outstanding invoices/bills for apply-to picker
  useEffect(() => {
    if (form.memo_type !== "credit" || !form.party_id) {
      setInvoices([]); return;
    }
    (async () => {
      try {
        const key = isAR ? "customer_id" : "vendor_id";
        const { data } = await api.get(applyEndpoint, { params: { [key]: form.party_id } });
        setInvoices((data || []).filter((d) => d.status !== "paid" && d.currency === form.currency));
      } catch { setInvoices([]); }
    })();
  }, [form.party_id, form.memo_type, form.currency, applyEndpoint, isAR]);

  const total = useMemo(() =>
    form.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0)
  , [form.lines]);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { _k: crypto.randomUUID(), description: "", quantity: "1", unit_price: "" }] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const patchLine = (i, patch) => setForm(f => ({
    ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l),
  }));

  const canSubmit = form.party_id && form.date && form.lines.length >= 1 &&
    form.lines.every(l => l.description && parseFloat(l.unit_price) > 0) && total > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = {
        [isAR ? "customer_id" : "vendor_id"]: form.party_id,
        memo_type: form.memo_type,
        date: form.date,
        currency: form.currency,
        reason: form.reason || null,
        lines: form.lines.map(l => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price),
        })),
      };
      if (form.apply_to_id && form.apply_to_id !== "none") {
        body[isAR ? "apply_to_invoice_id" : "apply_to_bill_id"] = form.apply_to_id;
      }
      const { data } = await api.post(endpoint, body);
      toast.success(`${data.number} posted · ${SYM[data.currency]}${data.total}`);
      setDlgOpen(false);
      setForm({
        party_id: "", memo_type: "credit", date: new Date().toISOString().slice(0, 10),
        currency: "USD", reason: "", apply_to_id: "none",
        lines: [{ _k: crypto.randomUUID(), description: "", quantity: "1", unit_price: "" }],
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post memo");
    } finally {
      setSaving(false);
    }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete ${m.number}? This reverses the linked journal entry.`)) return;
    try {
      await api.delete(`${endpoint}/${m.id}`);
      toast.success("Memo reversed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const openEmail = (m) => {
    // SEC-003: recipient is locked server-side to the party's stored email.
    // We just prefill for visibility — the backend ignores the `to` field.
    const party = parties.find((p) => p.id === m.party_id);
    setEmailMemo(m);
    setEmailForm({
      to: party?.email || "",
      subject: `${m.memo_type[0].toUpperCase()}${m.memo_type.slice(1)} Memo ${m.number} · Accountrix`,
      message: "",
    });
  };

  const sendEmail = async () => {
    if (!emailMemo) return;
    setSendingEmail(true);
    try {
      const { data } = await api.post(`${endpoint}/${emailMemo.id}/email`, {
        subject: emailForm.subject,
        message: emailForm.message,
      });
      toast.success(`Memo emailed to ${data.sent_to || "customer"}`);
      setEmailMemo(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email send failed");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
          {isAR ? "Accounts Receivable" : "Accounts Payable"}
        </div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-4xl mt-2">
              {isAR ? "AR" : "AP"} Credit & Debit Memos
            </h1>
            <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
              {isAR
                ? "Issue a credit memo to reduce what a customer owes, or a debit memo to add a charge. Auto-posts a balanced journal entry and applies to an outstanding invoice if you pick one."
                : "Record a vendor credit note that reduces what you owe, or a debit note for an additional charge. Journal entry posts automatically."}
            </p>
          </div>
          <Button
            data-testid={`new-${mode}-memo-btn`}
            className="btn-brand rounded-md h-11 px-5"
            onClick={() => setDlgOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" /> New memo
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="card-flat overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>{partyLabel}</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {memos.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <ScrollText className="w-8 h-8 mx-auto text-[var(--muted-text)] opacity-40" />
                  <p className="text-sm text-[var(--muted-text)] mt-3">No memos yet</p>
                </TableCell>
              </TableRow>
            )}
            {memos.map((m) => (
              <TableRow key={m.id} data-testid={`memo-row-${m.id}`}>
                <TableCell className="font-mono-data text-xs">{m.number}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                    m.memo_type === "credit"
                      ? "bg-[#EDF3EF] text-[#4C956C]"
                      : "bg-[#FBEEE7] text-[#B4283A]"
                  }`}>
                    {m.memo_type === "credit" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                    {m.memo_type}
                  </span>
                </TableCell>
                <TableCell>{m.party_name}</TableCell>
                <TableCell className="text-sm">{formatDate(m.date)}</TableCell>
                <TableCell className="text-right font-mono-data">
                  {formatCurrency(m.total, m.currency)}
                </TableCell>
                <TableCell className="text-xs text-[var(--muted-text)]">
                  {m.applied_amount > 0
                    ? `${formatCurrency(m.applied_amount, m.currency)} applied`
                    : <span className="opacity-40">unapplied</span>}
                </TableCell>
                <TableCell className="text-xs text-[var(--muted-text)] max-w-[180px] truncate" title={m.reason || ""}>
                  {m.reason || <span className="opacity-40">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEmail(m)} data-testid={`memo-email-${m.id}`} title="Email this memo">
                    <Mail className="w-4 h-4 text-[#2F4F4F]" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => del(m)} data-testid={`memo-del-${m.id}`}>
                    <Trash2 className="w-4 h-4 text-[#B4283A]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* New memo dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-xl" data-testid="memo-dialog">
          <DialogHeader>
            <DialogTitle>New {isAR ? "AR" : "AP"} Memo</DialogTitle>
            <DialogDescription>
              Choose credit (reduces balance) or debit (adds charge), pick the currency, and enter the line items. A balanced journal entry posts automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                  Type
                </Label>
                <Select value={form.memo_type} onValueChange={(v) => setForm({ ...form, memo_type: v })}>
                  <SelectTrigger data-testid="memo-type" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit (reduces balance)</SelectItem>
                    <SelectItem value="debit">Debit (adds charge)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                  Currency
                </Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v, apply_to_id: "none" })}>
                  <SelectTrigger data-testid="memo-currency" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="NGN">NGN (₦)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                  Date
                </Label>
                <Input type="date" data-testid="memo-date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-10 mt-1" />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                {partyLabel}
              </Label>
              <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v, apply_to_id: "none" })}>
                <SelectTrigger data-testid="memo-party" className="h-10 mt-1">
                  <SelectValue placeholder={`Pick a ${partyLabel.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.memo_type === "credit" && invoices.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                  Apply to {isAR ? "invoice" : "bill"} (optional)
                </Label>
                <Select value={form.apply_to_id} onValueChange={(v) => setForm({ ...form, apply_to_id: v })}>
                  <SelectTrigger data-testid="memo-apply" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don't apply (keep as credit)</SelectItem>
                    {invoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.number} · {formatCurrency(inv.total, inv.currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Lines */}
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {form.lines.map((line, i) => (
                <div key={line._k || i} className="grid grid-cols-[1fr_80px_100px_28px] gap-2 items-end">
                  <div>
                    {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Description</Label>}
                    <Input data-testid={`memo-line-desc-${i}`} value={line.description}
                      onChange={(e) => patchLine(i, { description: e.target.value })}
                      placeholder="e.g. Return of damaged goods" className="h-9 mt-1" />
                  </div>
                  <div>
                    {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Qty</Label>}
                    <Input data-testid={`memo-line-qty-${i}`} type="number" step="0.01" value={line.quantity}
                      onChange={(e) => patchLine(i, { quantity: e.target.value })}
                      className="h-9 mt-1 font-mono-data" />
                  </div>
                  <div>
                    {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Unit price</Label>}
                    <Input data-testid={`memo-line-price-${i}`} type="number" step="0.01" value={line.unit_price}
                      onChange={(e) => patchLine(i, { unit_price: e.target.value })}
                      placeholder="0.00" className="h-9 mt-1 font-mono-data" />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeLine(i)}
                    disabled={form.lines.length <= 1}
                    data-testid={`memo-line-remove-${i}`}
                    className="h-9 w-9 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between border-t border-[var(--divider)] pt-2">
              <Button variant="ghost" size="sm" onClick={addLine} data-testid="memo-add-line">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add line
              </Button>
              <div className="text-sm">
                <span className="text-[var(--muted-text)] mr-2">Total:</span>
                <span className="font-mono-data font-medium" data-testid="memo-total">
                  {formatCurrency(total, form.currency)}
                </span>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Reason (optional)</Label>
              <Textarea data-testid="memo-reason" value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Product return · billing correction · goodwill discount…" rows={2} className="mt-1" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} data-testid="memo-cancel">Cancel</Button>
            <Button className="btn-brand" onClick={submit} disabled={!canSubmit || saving} data-testid="memo-submit">
              {saving ? "Posting…" : "Post memo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={!!emailMemo} onOpenChange={(o) => !o && setEmailMemo(null)}>
        <DialogContent className="max-w-md" data-testid="memo-email-dialog">
          <DialogHeader>
            <DialogTitle>Email {emailMemo?.number}</DialogTitle>
            <DialogDescription>
              Send this memo as a styled HTML email. Requires Resend to be configured.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">To (locked to customer email)</Label>
              <Input data-testid="memo-email-to" value={emailForm.to}
                readOnly disabled
                placeholder="Set this customer's email first"
                className="h-10 mt-1 bg-[var(--hover)]" type="email" />
              {!emailForm.to && (
                <p className="text-xs text-[#B4283A] mt-1">This customer has no email on file — add one first.</p>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Subject</Label>
              <Input data-testid="memo-email-subject" value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                className="h-10 mt-1" maxLength={200} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Message (optional, plain text)</Label>
              <Textarea data-testid="memo-email-message" value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Add a personal note…" rows={3} className="mt-1" maxLength={2000} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailMemo(null)} data-testid="memo-email-cancel">Cancel</Button>
            <Button className="btn-brand" onClick={sendEmail}
              disabled={!emailForm.to || sendingEmail} data-testid="memo-email-send">
              {sendingEmail ? "Sending…" : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
